import { useState, useEffect } from "react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Clock, Calendar, ArrowDown, CheckCircle, FileEdit, PlusCircle, AlertCircle, StickyNote, Edit3, Trash2 } from "lucide-react";
import { HistoryChange, HistoryEntry } from "./lead-card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { ScrollArea } from "./ui/scroll-area";
import { getUserName } from "@/services/userService";

type LeadHistoryProps = {
  history?: HistoryEntry[];
};

// Field name translations
const FIELD_LABELS: Record<string, string> = {
  firstName: "Vorname",
  lastName: "Nachname",
  position: "Beruf",
  aufhaenger: "Aufhänger",
  attachment: "Aufhänger",
  email: "E-Mail",
  phone: "Telefon",
  status: "Status",
  priority: "Priorität",
  nextContactDate: "Nächster Kontakt",
  lastContactDate: "Letzter Kontakt",
  notes: "Notizen",
  notesHistory: "Notizen",
  assignedTo: "Zugewiesen an"
};

// Format a value for display
const formatValue = (key: string, value: any): string => {
  if (value === undefined || value === null) return "-";
  
  // Handle notesHistory array specially
  if (key === "notesHistory" && Array.isArray(value)) {
    if (value.length === 0) return "Keine Notizen";
    // Extract the content from NotesEntry objects and show the latest note content
    const contents = value
      .filter(note => note && typeof note === 'object' && note.content)
      .map(note => note.content.trim())
      .filter(content => content.length > 0);
    
    if (contents.length === 0) return "Keine Notizen";
    
    // Show first 100 characters of the latest note
    const latestContent = contents[0]; // First in array is usually latest
    return latestContent.length > 100 
      ? `"${latestContent.substring(0, 100)}..."` 
      : `"${latestContent}"`;
  }
  
  // Handle Firestore Timestamp objects
  if (value && typeof value === 'object' && value.seconds !== undefined && value.nanoseconds !== undefined) {
    try {
      // Convert Firestore Timestamp to Date
      const milliseconds = value.seconds * 1000 + value.nanoseconds / 1000000;
      const date = new Date(milliseconds);
      return format(date, "dd.MM.yyyy HH:mm", { locale: de });
    } catch (error) {
      console.error("Error formatting timestamp:", error, value);
      return String(value);
    }
  }
  
  if (key === "nextContactDate" || key === "lastContactDate") {
    if (!value) return "-";
    // Handle empty objects {} or invalid date formats
    if (typeof value === 'object' && !(value instanceof Date)) {
      // Check for Firestore Timestamp with toDate()
      if (typeof value.toDate === 'function') {
        try {
          return format(value.toDate(), "dd.MM.yyyy HH:mm", { locale: de });
        } catch (error) {
          console.error("Error formatting Firestore date:", error, value);
          return "-";
        }
      }
      // Check for __time__ format
      if (value.__time__) {
        try {
          return format(new Date(value.__time__), "dd.MM.yyyy HH:mm", { locale: de });
        } catch (error) {
          console.error("Error formatting __time__ date:", error, value);
          return "-";
        }
      }
      // Empty object {} or unknown format
      if (Object.keys(value).length === 0) {
        return "-";
      }
    }
    try {
      const date = typeof value === 'string' ? new Date(value) : value;
      if (isNaN(date.getTime())) return "-";
      return format(date, "dd.MM.yyyy HH:mm", { locale: de });
    } catch (error) {
      console.error("Error formatting date:", error, value);
      return "-";
    }
  }
  
  if (key === "status") {
    const statusLabels: Record<string, string> = {
      "new": "Neu",
      "old-contact": "Altkontakt",
      "not-reached": "Nicht erreicht",
      "follow-up": "Wiedervorlage",
      "scheduled": "Terminiert",
      "in-progress": "In Prozess",
      "won": "Gewonnen",
      "lost": "Verloren",
      "wrong-data": "Falsche Daten",
      "delete-data": "Daten löschen"
    };
    return statusLabels[value] || value;
  }
  
  if (key === "priority") {
    const priorityLabels: Record<string, string> = {
      "low": "Niedrig",
      "medium": "Mittel",
      "high": "Hoch"
    };
    return priorityLabels[value] || value;
  }
  
  return String(value);
};

// Helper function to determine icon based on history entry type
const getEntryIcon = (entry: HistoryEntry) => {
  switch (entry.type) {
    case "created":
      return <PlusCircle className="h-5 w-5 text-green-500" />;
    case "updated":
      return <FileEdit className="h-5 w-5 text-blue-500" />;
    case "deleted":
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    case "note_added":
      return <StickyNote className="h-5 w-5 text-purple-500" />;
    case "note_updated":
      return <Edit3 className="h-5 w-5 text-orange-500" />;
    case "note_deleted":
      return <Trash2 className="h-5 w-5 text-red-500" />;
    default:
      return <Clock className="h-5 w-5 text-muted-foreground" />;
  }
};

// Helper function to determine color based on history entry type
const getEntryColor = (entry: HistoryEntry): string => {
  switch (entry.type) {
    case "created":
      return "border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800";
    case "updated":
      return "border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800";
    case "deleted":
      return "border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800";
    case "note_added":
      return "border-purple-200 bg-purple-50 dark:bg-purple-950 dark:border-purple-800";
    case "note_updated":
      return "border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800";
    case "note_deleted":
      return "border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800";
    default:
      return "border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700";
  }
};

// Display title for entry type
const getEntryTitle = (entry: HistoryEntry): string => {
  switch (entry.type) {
    case "created":
      return "Erstellt";
    case "updated":
      return "Aktualisiert";
    case "deleted":
      return "Gelöscht";
    case "note_added":
      return "Notiz hinzugefügt";
    case "note_updated":
      return "Notiz bearbeitet";
    case "note_deleted":
      return "Notiz gelöscht";
    case "marketplace_assignment":
      return "Zugewiesen";
    default:
      return "Aktion";
  }
};

// Helper function to safely format date strings
const safeFormatHistoryDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return format(date, "dd.MM.yyyy HH:mm", { locale: de });
  } catch (error) {
    console.error("Error formatting history date:", error, dateString);
    return dateString;
  }
};

export function LeadHistory({ history }: LeadHistoryProps) {
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadUserNames = async () => {
      if (!history) return;
      
      const uniqueUserIds = [...new Set(history.map(entry => entry.user))];
      const names: Record<string, string> = {};
      
      for (const userId of uniqueUserIds) {
        try {
          const name = await getUserName(userId);
          names[userId] = name;
        } catch (error) {
          console.error("Error loading user name:", error);
          names[userId] = userId; // Fallback to user ID if name can't be loaded
        }
      }
      
      setUserNames(names);
    };
    
    loadUserNames();
  }, [history]);

  if (!history || history.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        Keine Verlaufseinträge vorhanden.
      </div>
    );
  }

  // Sort history by date (newest first)
  const sortedHistory = [...history].sort((a, b) => {
    try {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } catch (error) {
      console.error("Error sorting history:", error);
      return 0;
    }
  });

  return (
    <ScrollArea className="h-full">
      <div className="relative p-1 pb-4">
        {/* Vertical timeline line */}
        <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-border" />
        
        <div className="space-y-4">
          {sortedHistory.map((entry, index) => (
            <div 
              key={index} 
              className={`relative ml-8 pl-6 pr-4 py-3 rounded-md border ${getEntryColor(entry)} animate-fade-in`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Timeline dot */}
              <div className="absolute left-[-24px] top-3 flex items-center justify-center w-6 h-6 rounded-full bg-background border-2 border-border z-10">
                {getEntryIcon(entry)}
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 font-medium">
                  {getEntryTitle(entry)}
                </div>
                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5 mr-1" />
                  {safeFormatHistoryDate(entry.date)}
                </div>
              </div>
              
              {entry.type === "created" && (
                <div className="text-sm">Lead wurde erstellt</div>
              )}
              
              {(entry.type === "note_added" || entry.type === "note_updated" || entry.type === "note_deleted") && (
                <div className="text-sm">
                  <div className="italic text-muted-foreground mb-1">
                    {entry.noteContent && entry.noteContent.length > 100 
                      ? `"${entry.noteContent.substring(0, 100)}..."` 
                      : `"${entry.noteContent || ""}"`}
                  </div>
                  {entry.type === "note_updated" && entry.changes && entry.changes.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <span>Vorher: </span>
                      <span className="italic">
                        {entry.changes[0]?.oldValue && entry.changes[0].oldValue.length > 50 
                          ? `"${entry.changes[0].oldValue.substring(0, 50)}..."` 
                          : `"${entry.changes[0]?.oldValue || ""}"`}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {entry.type === "updated" && entry.changes && entry.changes.length > 0 && (
                <Collapsible className="w-full">
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm py-1 px-0 hover:bg-transparent">
                    <span>{entry.changes.length} Änderung{entry.changes.length !== 1 ? 'en' : ''}</span>
                    <ArrowDown className="h-4 w-4 text-muted-foreground transition-transform ui-open:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 mt-2 text-sm">
                    {entry.changes.map((change, idx) => (
                      <div key={idx} className="border-l-2 border-l-primary pl-3 py-1 animate-fade-in">
                        <div className="font-medium">{FIELD_LABELS[change.field] || change.field}</div>
                        <div className="grid grid-cols-[auto_1fr] gap-x-2 items-center text-muted-foreground">
                          <span>Von:</span> 
                          <span>{formatValue(change.field, change.oldValue)}</span>
                          <span>Auf:</span> 
                          <span className="font-medium text-foreground">{formatValue(change.field, change.newValue)}</span>
                        </div>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
              
              <div className="text-xs text-muted-foreground mt-2 flex items-center">
                <span className="mr-1">Von:</span> {userNames[entry.user] || entry.user}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}
